import { useMutation, useQuery } from "@apollo/client";
import { gql } from "../../__generated__/gql";
import { useNavigate } from "react-router-dom";
import BACK_IMG from "../../components/common/back-img.png";
import Stamp from "../../components/frequency/stamp";
import {
  HiShoppingBag,
  HiStar,
  HiChevronDown,
  HiChevronUp,
} from "react-icons/hi";
import { FrequencyReason } from "../../__generated__/graphql";
import { notify } from "../../utils/toast";
import { getFrequencyStartIndex } from "../../utils/frequency";
import { useAuth } from "../../core/auth";
import { useEffect, useState } from "react";
import Lottie from "lottie-react";
import presentAnimation from "../../assets/lottie/present.json";
import { HiGift, HiSparkles } from "react-icons/hi2";
import { useGuestMode } from "../../hooks/use-guest-mode";

export const FREQUENCY_QUERY = gql(`
  query Frequency {
    me {
      frequencyCount
      frequency {
        id
        reason
        createdAt
      }
    }
  }
`);

export const ISSUE_USER_FREQUENCY_COUPON = gql(`
  mutation issueUserFrequencyCoupon {
    issueUserFrequencyCoupon {
      ok
      error
    }
  }
`);

interface StampRowProps {
  stamps: Array<{
    id: number;
    reason: FrequencyReason;
    createdAt: string;
  } | null>;
  startIndex: number;
}

export const StampRow = ({ stamps, startIndex }: StampRowProps) => (
  <div className="grid grid-cols-10 gap-2 justify-items-center">
    {stamps
      .slice(startIndex, startIndex + 10)
      .map((stamp, idx) =>
        stamp ? (
          <Stamp
            key={stamp.id}
            date={stamp.createdAt}
            reason={stamp.reason}
            index={startIndex + idx}
          />
        ) : (
          <Stamp
            key={`empty-${startIndex + idx}`}
            isEmpty
            index={startIndex + idx}
          />
        )
      )}
  </div>
);

const Frequency = () => {
  const { isLoggedIn } = useAuth();
  const { isGuestWithSource } = useGuestMode();

  const { data: frequencyData } = useQuery(FREQUENCY_QUERY, {
    skip: !isLoggedIn,
  });
  const [issueUserFrequencyCoupon] = useMutation(ISSUE_USER_FREQUENCY_COUPON);
  const navigate = useNavigate();
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [animationStart, setAnimationStart] = useState(false);
  const stamps = frequencyData?.me?.frequency || [];
  const validStamps = stamps.filter(
    (stamp) =>
      stamp.reason === "ORDER" ||
      stamp.reason === "REVIEW" ||
      stamp.reason === "EVENT" ||
      stamp.reason === "SIGNUP"
  );

  // 로그인하지 않았지만 source를 입력한 경우 가짜 frequency 2개 표시
  const totalCount = isLoggedIn
    ? validStamps.length
    : isGuestWithSource
    ? 2
    : 0;
  const currentCycleCount = getFrequencyStartIndex(totalCount, 20); // 현재 사이클의 스탬프 개수
  const lastCycleStamps = validStamps.slice(currentCycleCount); // 마지막 사이클의 스탬프들

  // 가짜 frequency 2개 생성 (게스트 모드용)
  const mockStamps = isGuestWithSource
    ? [
        {
          id: -1,
          reason: "EVENT" as FrequencyReason,
          createdAt: new Date().toISOString(),
        },
        {
          id: -2,
          reason: "EVENT" as FrequencyReason,
          createdAt: new Date().toISOString(),
        },
      ]
    : [];

  // 현재 사이클의 스탬프만 표시하고 나머지는 빈 스탬프(null)로 채움
  const displayStamps = Array.from({ length: 20 }, (_, index) => {
    if (isLoggedIn) {
      return index < currentCycleCount + 20 ? lastCycleStamps[index] : null;
    } else if (isGuestWithSource) {
      return index < 2 ? mockStamps[index] : null;
    } else {
      return null;
    }
  });

  useEffect(() => {
    if (!isLoggedIn) {
      notify("로그인이 필요한 기능이에요!");
    }
  }, [isLoggedIn]);

  return (
    <div className="w-full h-screen bg-white overflow-y-scroll">
      <div className="px-4 bg-white">
        <img
          src={BACK_IMG}
          onClick={() => navigate(-1)}
          className="absolute top-5 left-4 w-[40px] h-[40px] z-50"
          alt="back"
        />
      </div>

      <div className="flex flex-col gap-6 p-4 mt-14">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-blue-100">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            내 스탬프
          </h2>
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-baseline justify-center gap-0.5">
              <span className="text-4xl font-bold text-primary">
                {totalCount % 10}
              </span>
              <span className="text-lg text-primary/80">/</span>
              <span className="text-lg text-primary/80">10</span>
            </div>
            <button
              className="w-full py-4 bg-primary text-white font-semibold rounded-2xl text-lg hover:bg-blue-600 active:bg-blue-700 transition-colors duration-200 shadow-lg"
              disabled={!isLoggedIn && !isGuestWithSource}
              onClick={async () => {
                if (!isLoggedIn) {
                  notify("로그인 후 쿠폰을 받으실 수 있어요!");
                  navigate("/login");
                  return;
                }

                await issueUserFrequencyCoupon({
                  onCompleted: (data) => {
                    if (data?.issueUserFrequencyCoupon.ok) {
                      setAnimationStart(true);
                    } else {
                      notify(
                        data?.issueUserFrequencyCoupon.error ||
                          "쿠폰 발급에 실패했어요"
                      );
                    }
                  },
                  onError: (error) => {
                    notify(error.message);
                  },
                });
              }}
            >
              {isGuestWithSource ? "로그인 후 쿠폰 받기" : "1,000원 쿠폰 받기"}
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-blue-100">
          <div className="flex justify-between items-center mb-4 px-4 pt-4">
            <h2 className="text-xl font-semibold text-gray-800">스탬프 모음</h2>
            <div className="flex flex-wrap justify-end items-center gap-x-2 gap-y-2 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-md text-blue-500">
                    <HiShoppingBag />
                  </span>
                </div>
                <span>주문</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-md text-yellow-500">
                    <HiStar />
                  </span>
                </div>
                <span>포토리뷰</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-md text-green-500">
                    <HiGift className="text-green-500" />
                  </span>
                </div>
                <span>이벤트</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 bg-violet-100 rounded-full flex items-center justify-center">
                  <HiSparkles className="w-3.5 h-3.5 text-violet-600" />
                </div>
                <span>가입</span>
              </div>
            </div>
          </div>

          <div className="rounded-xl py-4 px-5">
            <div className="flex flex-col gap-4">
              <StampRow stamps={displayStamps} startIndex={0} />
              <StampRow stamps={displayStamps} startIndex={10} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl px-4 py-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            스탬프 안내
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-2">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-2xl text-blue-500">
                  <HiShoppingBag />
                </span>
              </div>
              <p className="text-base text-gray-600">
                6,900원 이상 주문 시 스탬프 1개가 적립되며, 주문당 최대 1개만
                적립됩니다.
              </p>
            </div>
            <div className="flex items-center gap-2 p-2">
              <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center flex-shrink-0">
                <HiSparkles className="w-5 h-5 text-violet-600" />
              </div>
              <p className="text-base text-gray-600">
                회원가입 시 웰컴 스탬프가 적립돼요
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 p-2">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl text-yellow-500">
                    <HiStar />
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <p className="text-base text-gray-600">
                    주문한 상품에 포토 리뷰를 작성하면 스탬프 1개가 적립됩니다.
                    구매 확정 후 2일 이내 작성 시에만 적립됩니다.
                  </p>
                  <button
                    onClick={() => setIsGuideOpen(!isGuideOpen)}
                    className="flex items-center gap-1 text-sm font-medium"
                  >
                    스탬프가 안 들어왔나요?
                    <span className="font-bold">
                      {isGuideOpen ? <HiChevronUp /> : <HiChevronDown />}
                    </span>
                  </button>
                </div>
              </div>
              {isGuideOpen && (
                <div className="bg-yellow-50 rounded-lg p-3 text-sm text-gray-600 border border-yellow-100 ml-10">
                  <div className="flex flex-col gap-1 leading-5">
                    <p>
                      구매 확정 후 2일이 지난 뒤 작성한 포토리뷰는 스탬프가
                      적립되지 않습니다.
                    </p>
                    <p>
                      작성해주신 리뷰는 사진과 내용에 부적절한 표현이 없는지
                      확인 과정을 거친 뒤 스탬프가 발급됩니다.
                    </p>
                    <p>
                      리뷰 작성 직후 약간의 처리 시간이 걸릴 수 있지만, 자동으로
                      확인이 완료되면 스탬프를 발급해드릴게요!
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="pt-2 border-t border-gray-100">
              <ul className="text-base text-gray-600 space-y-2 list-disc pl-5">
                <li>
                  ※ 1,000원 쿠폰은 발급일로부터 30일 이내 사용 가능합니다.
                </li>
                <li>
                  스탬프 10개를 모으면 1,000원 쿠폰으로 교환할 수 있습니다.
                </li>
                <li>
                  포토리뷰 스탬프는 구매 확정 후 2일 이내 작성 시 적립됩니다.
                </li>
                <li>쿠폰은 마이페이지의 [내 쿠폰함]에서 확인하실 수 있어요</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`fixed top-0 left-0 w-full h-full bg-black/60 z-50 flex items-center justify-center ${
          animationStart ? "animate-fadeIn" : "hidden"
        }`}
      >
        <div className="flex flex-col items-center justify-center p-4 max-w-sm w-full text-center">
          <div className="mb-8">
            <Lottie
              animationData={presentAnimation}
              loop={true}
              className="w-96 h-96 mx-auto"
            />
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">축하해요! 🎉</h2>
            <p className="text-lg text-white/90 leading-relaxed">
              스탬프{" "}
              <span className="font-bold text-yellow-300">{totalCount}개</span>
              를 모아
              <br />
              <span className="font-bold text-yellow-300">1,000원 쿠폰</span>이
              발급되었습니다!
            </p>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-center gap-2 text-white/80">
              <HiShoppingBag className="w-5 h-5" />
              <span className="font-medium">
                마이페이지 → 내 쿠폰함에서 확인하세요
              </span>
            </div>
          </div>

          <button
            onClick={() => setAnimationStart(false)}
            className="w-full bg-primary text-white font-semibold py-4 rounded-2xl text-lg hover:bg-blue-600 active:bg-blue-700 transition-colors duration-200 shadow-lg"
          >
            확인하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default Frequency;
